import { EventService } from '@host/services/event.service';
// import { EventTypes } from 'nexgen-types'; // Commented out until ApplicationErrorEvent is fixed
import { BaseEvent } from 'nexgen-types/events';
import { voLogger } from '@shared/utils/logger';

export abstract class BaseEventRouter {
  protected _log: ReturnType<typeof voLogger>;

  constructor(
    protected eventService: EventService,
    protected routerName: string
  ) {
    this._log = voLogger(`${routerName.toLowerCase()}:event-router`);
  }

  /**
   * Emit an application error event with router context
   */
  emitApplicationError(message: string, error: any, additionalContext?: any): void {
    this._log('❌ Emitting application error:', message, error);

    // TODO: Fix ApplicationErrorEvent - this event type doesn't exist in EventTypes enum
    // this.eventService.emit(EventTypes.ApplicationErrorEvent, {
    //   source: this.routerName,
    //   message,
    //   error: error.message || error,
    //   context: additionalContext,
    //   timestamp: new Date().toISOString(),
    // });

    // For now, just log the error
    console.error(`[${this.routerName}] Application Error:`, { message, error, additionalContext });
  }

  /**
   * Check if object looks like a structured event (duck typing approach)
   */
  private isStructuredEvent(obj: any): boolean {
    return (
      obj &&
      typeof obj === 'object' &&
      typeof obj.__type === 'string' &&
      // If it has event-specific fields, treat it as a structured event
      (obj.user_pid !== undefined ||
        obj.room_pid !== undefined ||
        obj.floor_pid !== undefined ||
        obj.org_pid !== undefined ||
        obj.meeting_pid !== undefined)
    );
  }

  /**
   * Emit any event through this router (smart routing for BaseEvent vs plain objects)
   */
  emitEvent(eventType: string, eventData: any): void {
    this._log('📤 Emitting event:', eventType, eventData);

    // 🔍 DEBUG: Let's see what we're actually dealing with
    console.log('🔍 [BaseEventRouter] eventData type:', typeof eventData);
    console.log('🔍 [BaseEventRouter] eventData constructor:', eventData?.constructor?.name);
    console.log(
      '🔍 [BaseEventRouter] eventData instanceof BaseEvent:',
      eventData instanceof BaseEvent
    );
    console.log('🔍 [BaseEventRouter] isStructuredEvent:', this.isStructuredEvent(eventData));

    // 🎯 SMART ROUTING: Check if eventData looks like a structured event
    if (this.isStructuredEvent(eventData)) {
      // Structured event - use emitTyped to preserve the instance
      this._log('✅ Using emitTyped for structured event');
      this.eventService.emitTyped(eventData, true); // ✅ Re-enable socket broadcasting
    } else {
      // Plain object - use emit to wrap in DynamicEvent
      this._log('🔄 Using emit for plain object');
      this.eventService.emit(eventType, eventData, true); // ✅ Re-enable socket broadcasting
    }
  }

  /**
   * Handle errors in event processing with consistent logging
   */
  protected handleEventError(eventType: string, error: any, eventData?: any): void {
    const message = `Failed to handle ${eventType} event`;
    this._log('❌ Event handling error:', message, error, eventData);

    this.emitApplicationError(message, error, {
      eventType,
      eventData,
      handlerContext: this.routerName,
    });
  }

  /**
   * Create a filtered event handler that only executes for specific user types
   */
  protected createUserTypeFilter(
    handler: (event: any) => void,
    userTypeCheck: () => boolean,
    eventTypeName: string
  ): (event: any) => void {
    return (event: any) => {
      if (userTypeCheck()) {
        handler(event);
      } else {
        console.log(
          `🚫 ~ ${this.constructor.name} ~ Filtering ${eventTypeName} event - user type check failed`
        );
      }
    };
  }

  /**
   * Abstract method - each router must implement its own registration
   */
  abstract registerEventListeners(): void;

  /**
   * Abstract method - each router should implement cleanup
   */
  abstract unregisterEventListeners(): void;
}
